// @ts-nocheck
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {
  useAddGameFeedComment,
  useAddGameFeedReply,
  useDeleteGameFeedComment,
  useGetGameFeedComments,
  useGetGameFeedReplies,
} from "../../../../hooks/games/useGameFeedComments.hook";
import useGetUserInfo from "../../../../hooks/useGetUserInfo";
import {useAuthStore} from "../../../../store/useAuth.store";
import * as styles from "../_gameFeedCls";

const timeAgo = dateStr => {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
};

// ----- Reply Thread (Instagram-style expand/collapse) -----

const ReplyThread = ({comment, userId, userAlias, isAuthenticated, onDelete}) => {
  const {t} = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [showReplyInput, setShowReplyInput] = useState(false);
  const replyInputRef = useRef(null);

  const {replies, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading} = useGetGameFeedReplies(
    comment.ID,
    isExpanded
  );
  const {addReply, isAddingReply, replyModerationReason, clearReplyModerationError} = useAddGameFeedReply();

  useEffect(() => {
    if (showReplyInput) replyInputRef.current?.focus();
  }, [showReplyInput]);

  const handleReplySubmit = e => {
    e.preventDefault();
    const trimmed = replyText.trim();
    if (!trimmed || isAddingReply) return;

    clearReplyModerationError();
    addReply({commentId: comment.ID, content: trimmed, userAlias});
    setReplyText("");
    setShowReplyInput(false);
    if (!isExpanded) setIsExpanded(true);
  };

  const replyCount = comment.reply_count ?? 0;

  return (
    <div className={styles.commentThreadWrapperCls}>
      {/* --- Action row: Reply button + View replies toggle --- */}
      <div className={styles.commentActionsCls}>
        <span className={styles.commentTimeCls}>{timeAgo(comment.created_at)}</span>
        {isAuthenticated && (
          <button
            type="button"
            className={styles.commentReplyBtnCls}
            onClick={() => {
              clearReplyModerationError();
              setShowReplyInput(prev => !prev);
            }}
          >
            {t("gameFeedReply")}
          </button>
        )}
        {userId && comment.user_id === userId && (
          <button
            type="button"
            className={styles.commentDeleteBtnCls}
            onClick={() => onDelete(comment.ID)}
          >
            {t("gameFeedDelete")}
          </button>
        )}
      </div>

      {/* --- View / hide replies toggle --- */}
      {replyCount > 0 && (
        <button
          type="button"
          className={styles.viewRepliesBtnCls}
          onClick={() => setIsExpanded(prev => !prev)}
        >
          <span className={styles.viewRepliesLineCls} />
          {isExpanded ? t("gameFeedHideReplies") : t("gameFeedViewReplies", {count: replyCount})}
        </button>
      )}

      {/* --- Expanded replies list --- */}
      {isExpanded && (
        <div className={styles.repliesContainerCls}>
          {isLoading && (
            <div
              className={styles.feedLoadingGameCls}
              style={{padding: "var(--space-1)"}}
            >
              <div
                className={styles.feedLoadingSpinnerCls}
                style={{width: 18, height: 18}}
              />
            </div>
          )}

          {replies.map(reply => (
            <div
              key={reply.ID}
              className={styles.commentItemCls}
            >
              <div
                className={styles.commentAvatarCls}
                style={{width: "var(--size-6)", height: "var(--size-6)", fontSize: "var(--font-eyebrow)"}}
              >
                {reply.user_alias?.[0]?.toUpperCase() || "?"}
              </div>
              <div className={styles.commentBodyCls}>
                <span className={styles.commentAliasCls}>{reply.user_alias}</span>
                <span className={styles.commentContentCls}>{reply.content}</span>
                <div className={styles.commentActionsCls}>
                  <span className={styles.commentTimeCls}>{timeAgo(reply.created_at)}</span>
                  {userId && reply.user_id === userId && (
                    <button
                      type="button"
                      className={styles.commentDeleteBtnCls}
                      onClick={() => onDelete(reply.ID)}
                    >
                      {t("gameFeedDelete")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {hasNextPage && (
            <button
              type="button"
              className={styles.viewRepliesBtnCls}
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              <span className={styles.viewRepliesLineCls} />
              {isFetchingNextPage ? "..." : t("gameFeedViewMoreReplies")}
            </button>
          )}
        </div>
      )}

      {/* --- Inline reply input --- */}
      {showReplyInput && (
        <div className={styles.replyInputContainerCls}>
          {replyModerationReason && (
            <div
              className={styles.moderationWarningCls}
              style={{padding: "4px 8px", fontSize: "var(--font-caption)"}}
            >
              ⚠️{" "}
              {replyModerationReason === "support_redirect"
                ? t("gameFeedCommentSupportRedirect")
                : t("gameFeedCommentModerated")}
            </div>
          )}
          <form
            className={styles.replyInputWrapperCls}
            onSubmit={handleReplySubmit}
          >
            <input
              ref={replyInputRef}
              type="text"
              className={styles.commentInputCls}
              placeholder={t("gameFeedReplyPlaceholder")}
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              maxLength={500}
            />
            <button
              type="submit"
              className={styles.commentSubmitBtnCls}
              disabled={!replyText.trim() || isAddingReply}
            >
              {t("gameFeedPost")}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

// ----- Main Comments Panel -----

const CommentsPanel = ({provider, gameId, onClose}) => {
  const {t} = useTranslation();
  const authStatus = useAuthStore(state => state.authStatus);
  const isAuthenticated = authStatus === "authenticated";
  const {data: userData} = useGetUserInfo();
  const userAlias = userData?.alias || userData?.username || "Player";
  const userId = userData?.id;

  const [commentText, setCommentText] = useState("");
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const {comments, total, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading} = useGetGameFeedComments(
    provider,
    gameId,
    true
  );
  const {addComment, isAdding, moderationReason, clearModerationError} = useAddGameFeedComment();
  const {deleteComment} = useDeleteGameFeedComment();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = e => {
    e.preventDefault();
    const trimmed = commentText.trim();
    if (!trimmed || isAdding) return;

    clearModerationError();
    addComment({gameId, gameProvider: provider, content: trimmed, userAlias});
    setCommentText("");
  };

  const handleDelete = commentId => {
    deleteComment(commentId);
  };

  const handleScroll = () => {
    if (!listRef.current || !hasNextPage || isFetchingNextPage) return;
    const {scrollTop, scrollHeight, clientHeight} = listRef.current;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchNextPage();
    }
  };

  return (
    <>
      <div
        className={styles.commentOverlayCls}
        onClick={onClose}
        role="presentation"
      />
      <div className={styles.commentsPanelCls}>
        <div className={styles.commentsPanelHeaderCls}>
          <h3 className={styles.commentsPanelTitleCls}>
            {t("gameFeedComments")} {total > 0 && `(${total})`}
          </h3>
          <button
            type="button"
            className={styles.commentsPanelCloseCls}
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        <div
          className={styles.commentsListCls}
          ref={listRef}
          onScroll={handleScroll}
        >
          {isLoading && (
            <div className={styles.feedLoadingGameCls}>
              <div className={styles.feedLoadingSpinnerCls} />
            </div>
          )}

          {!isLoading && comments.length === 0 && (
            <p style={{color: "rgba(255,255,255,0.4)", textAlign: "center", fontSize: "var(--font-body)"}}>
              {t("gameFeedNoComments")}
            </p>
          )}

          {comments.map(comment => (
            <div
              key={comment.ID}
              className={styles.commentItemCls}
            >
              <div className={styles.commentAvatarCls}>{comment.user_alias?.[0]?.toUpperCase() || "?"}</div>
              <div className={styles.commentBodyCls}>
                <span className={styles.commentAliasCls}>{comment.user_alias}</span>
                <span className={styles.commentContentCls}>{comment.content}</span>
                <ReplyThread
                  comment={comment}
                  userId={userId}
                  userAlias={userAlias}
                  isAuthenticated={isAuthenticated}
                  onDelete={handleDelete}
                />
              </div>
            </div>
          ))}

          {isFetchingNextPage && (
            <div
              className={styles.feedLoadingGameCls}
              style={{padding: "var(--space-2-5)"}}
            >
              <div
                className={styles.feedLoadingSpinnerCls}
                style={{width: "var(--size-6)", height: "var(--size-6)"}}
              />
            </div>
          )}
        </div>

        {isAuthenticated ? (
          <>
            {moderationReason && (
              <div className={styles.moderationWarningCls}>
                ⚠️{" "}
                {moderationReason === "support_redirect"
                  ? t("gameFeedCommentSupportRedirect")
                  : t("gameFeedCommentModerated")}
              </div>
            )}
            <form
              className={styles.commentInputWrapperCls}
              onSubmit={handleSubmit}
            >
              <div
                className={styles.commentAvatarCls}
                style={{width: "var(--size-7)", height: "var(--size-7)", fontSize: "var(--font-caption)"}}
              >
                {userAlias[0]?.toUpperCase()}
              </div>
              <input
                ref={inputRef}
                type="text"
                className={styles.commentInputCls}
                placeholder={t("gameFeedAddComment")}
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                maxLength={500}
              />
              <button
                type="submit"
                className={styles.commentSubmitBtnCls}
                disabled={!commentText.trim() || isAdding}
              >
                {t("gameFeedPost")}
              </button>
            </form>
          </>
        ) : (
          <div className={styles.feedLoginPromptCls}>
            <span>{t("gameFeedLoginToComment")}</span>
          </div>
        )}
      </div>
    </>
  );
};

export default CommentsPanel;
